import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const SALES_EMAIL = "ventas@licit-ai.com";

export default function LoginPage() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [requestData, setRequestData] = useState({
    name: "", company: "", email: "", phone: "", plan: "pyme"
  });
  const [plans, setPlans] = useState([]);
  const [requestSent, setRequestSent] = useState(false);
  const [activeTab, setActiveTab] = useState("login");

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data.plans || [])).catch(() => {});
  }, []);

  if (user) {
    navigate("/", { replace: true });
    return null;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(loginData.email, loginData.password);
      toast.success("Sesión iniciada correctamente");
      if (res?.must_change_password) {
        navigate("/cambiar-password", { replace: true, state: { forced: true } });
      } else {
        navigate("/", { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  const handleRequest = async (e) => {
    e.preventDefault();
    if (!requestData.name || !requestData.company || !requestData.email || !requestData.phone || !requestData.plan) {
      toast.error("Por favor completa todos los campos");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", {
        name: requestData.name.trim(),
        company: requestData.company.trim(),
        email: requestData.email.trim(),
        phone: requestData.phone.trim(),
        plan: requestData.plan,
        billing_cycle: "monthly",
      });
      setRequestSent(true);
      toast.success("¡Solicitud enviada!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar la solicitud. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const resetRequest = () => {
    setRequestSent(false);
    setRequestData({ name: "", company: "", email: "", phone: "", plan: "pyme" });
    setActiveTab("login");
  };

  return (
    <div className="flex h-screen" data-testid="login-page">
      {/* Left panel - image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-[#09090B]">
        <img
          src="https://images.unsplash.com/photo-1571624762224-e58ef9009a12?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhYnN0cmFjdCUyMGFyY2hpdGVjdHVyZSUyMGdlb21ldHJpY3xlbnwwfHx8fDE3NzQyOTY2NDZ8MA&ixlib=rb-4.1.0&q=85"
          alt="Arquitectura moderna"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="relative z-10 flex flex-col justify-between p-16 w-full">
          <div>
            <h1 className="font-heading font-black text-5xl lg:text-6xl text-white tracking-tight leading-none">
              LICITA<span className="text-[#002FA7]">AI</span>
            </h1>
            <p className="mt-3 text-white/50 font-mono text-sm">v1.0 — Plataforma de Inteligencia</p>
          </div>
          <div className="space-y-8">
            <div className="border-l-2 border-[#002FA7] pl-6">
              <p className="text-white/90 text-lg font-medium leading-relaxed">
                Automatiza la búsqueda de licitaciones gubernamentales con inteligencia artificial
              </p>
            </div>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <p className="text-[#002FA7] font-heading font-light text-4xl">AI</p>
                <p className="text-white/50 text-xs font-mono mt-1">GPT-5.2</p>
              </div>
              <div>
                <p className="text-[#2A9D8F] font-heading font-light text-4xl">24/7</p>
                <p className="text-white/50 text-xs font-mono mt-1">MONITOREO</p>
              </div>
              <div>
                <p className="text-white font-heading font-light text-4xl">GT</p>
                <p className="text-white/50 text-xs font-mono mt-1">GUATECOMPRAS</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-10">
            <h1 className="font-heading font-black text-3xl tracking-tight">
              LICITA<span className="text-[#002FA7]">AI</span>
            </h1>
          </div>

          <div className="mb-6 text-right">
            <button
              type="button"
              onClick={() => navigate("/precios")}
              className="text-xs font-mono uppercase tracking-wider text-[#002FA7] hover:underline"
              data-testid="view-plans-link"
            >
              Ver planes y precios →
            </button>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full rounded-none bg-secondary h-12" data-testid="auth-tabs">
              <TabsTrigger value="login" className="rounded-none flex-1 font-medium data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid="login-tab">
                Iniciar Sesión
              </TabsTrigger>
              <TabsTrigger value="request" className="rounded-none flex-1 font-medium data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid="request-access-tab">
                Solicitar acceso
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-8">
              <form onSubmit={handleLogin} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Email</Label>
                  <Input
                    id="login-email"
                    data-testid="login-email-input"
                    type="email"
                    placeholder="correo@empresa.com"
                    className="rounded-none h-12 bg-card border-border focus-visible:ring-1 focus-visible:ring-[#002FA7] focus-visible:ring-offset-0"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Contraseña</Label>
                  <Input
                    id="login-password"
                    data-testid="login-password-input"
                    type="password"
                    placeholder="••••••••"
                    className="rounded-none h-12 bg-card border-border focus-visible:ring-1 focus-visible:ring-[#002FA7] focus-visible:ring-offset-0"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="login-submit-button"
                  className="w-full h-12 rounded-none bg-[#002FA7] hover:bg-[#001D66] text-white font-bold tracking-wide transition-colors duration-200"
                  disabled={loading}
                >
                  {loading ? "Ingresando..." : "INGRESAR"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="request" className="mt-8">
              {requestSent ? (
                <div className="space-y-6" data-testid="request-success-panel">
                  <div className="flex items-start gap-3 border border-[#2A9D8F]/40 bg-[#2A9D8F]/5 p-5">
                    <CheckCircle size={28} weight="fill" className="text-[#2A9D8F] shrink-0 mt-0.5" />
                    <div>
                      <p className="font-heading font-bold text-base leading-tight">Solicitud enviada</p>
                      <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                        Recibimos tu solicitud de acceso. Nuestro equipo de ventas te contactará en las próximas <strong>24 horas hábiles</strong> para activar tu cuenta.
                      </p>
                      <p className="text-xs font-mono text-muted-foreground mt-3">
                        ¿Dudas? Escríbenos a <a href={`mailto:${SALES_EMAIL}`} className="text-[#002FA7] underline">{SALES_EMAIL}</a>
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetRequest}
                    className="w-full h-12 rounded-none border-border font-medium"
                    data-testid="request-back-button"
                  >
                    Volver al inicio de sesión
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleRequest} className="space-y-4" data-testid="request-access-form">
                  <p className="text-xs text-muted-foreground leading-relaxed -mt-2 mb-2">
                    LicitaAI es por invitación. Cuéntanos quién eres y un asesor te activará el acceso.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="req-name" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Nombre</Label>
                    <Input
                      id="req-name"
                      data-testid="request-name-input"
                      placeholder="Tu nombre completo"
                      className="rounded-none h-11 bg-card border-border focus-visible:ring-1 focus-visible:ring-[#002FA7] focus-visible:ring-offset-0"
                      value={requestData.name}
                      onChange={(e) => setRequestData({ ...requestData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="req-company" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Empresa</Label>
                    <Input
                      id="req-company"
                      data-testid="request-company-input"
                      placeholder="Nombre de tu empresa"
                      className="rounded-none h-11 bg-card border-border focus-visible:ring-1 focus-visible:ring-[#002FA7] focus-visible:ring-offset-0"
                      value={requestData.company}
                      onChange={(e) => setRequestData({ ...requestData, company: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="req-email" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Email corporativo</Label>
                    <Input
                      id="req-email"
                      data-testid="request-email-input"
                      type="email"
                      placeholder="correo@empresa.com"
                      className="rounded-none h-11 bg-card border-border focus-visible:ring-1 focus-visible:ring-[#002FA7] focus-visible:ring-offset-0"
                      value={requestData.email}
                      onChange={(e) => setRequestData({ ...requestData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="req-phone" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Teléfono</Label>
                    <Input
                      id="req-phone"
                      data-testid="request-phone-input"
                      placeholder="+502 0000 0000"
                      className="rounded-none h-11 bg-card border-border focus-visible:ring-1 focus-visible:ring-[#002FA7] focus-visible:ring-offset-0"
                      value={requestData.phone}
                      onChange={(e) => setRequestData({ ...requestData, phone: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="req-plan" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Plan de interés</Label>
                    <Select
                      value={requestData.plan}
                      onValueChange={(v) => setRequestData({ ...requestData, plan: v })}
                    >
                      <SelectTrigger
                        id="req-plan"
                        data-testid="request-plan-select"
                        className="rounded-none h-11 bg-card border-border focus:ring-1 focus:ring-[#002FA7] focus:ring-offset-0"
                      >
                        <SelectValue placeholder="Elige un plan" />
                      </SelectTrigger>
                      <SelectContent>
                        {(plans.length ? plans : [
                          { id: "starter", name: "Starter" },
                          { id: "pyme", name: "PYME" },
                          { id: "growth", name: "Growth" },
                          { id: "enterprise", name: "Enterprise" },
                        ]).map((p) => (
                          <SelectItem key={p.id} value={p.id} data-testid={`request-plan-option-${p.id}`}>
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="submit"
                    data-testid="request-submit-button"
                    className="w-full h-12 rounded-none bg-[#002FA7] hover:bg-[#001D66] text-white font-bold tracking-wide transition-colors duration-200 mt-2"
                    disabled={loading}
                  >
                    {loading ? "Enviando..." : "SOLICITAR ACCESO"}
                  </Button>
                </form>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
